import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import type { CustomerIndexItemDto } from '@techgitter/contracts';
import type { WorkItem } from './workItemTypes';
import { CustomerLookupControl } from '../tickets/Customer/CustomerLookupControl';
import { changeWorkItemCustomer } from '../../services/workItemDataService';
import { PRIMARY_BTN_SX } from '../../styles/designSystem';

interface WorkItemChangeCustomerDialogProps {
  open: boolean;
  workItem: WorkItem;
  onClose: () => void;
  /** Called with the refreshed work item after a successful change. */
  onChanged: (updated: WorkItem) => void;
}

/**
 * Reassign a job/ticket to a different customer. Picking a customer posts to
 * `/api/workitems/{guid}/customer`, which updates RepairShopr first (source of
 * truth) then the Dataverse job mirror, and broadcasts workitems:changed.
 *
 * Modal (not a right panel) per the design system: a single-selection action
 * where the underlying page content is irrelevant (skill §6). Mirrors the
 * invoice customer-change dialog.
 */
export const WorkItemChangeCustomerDialog: React.FC<WorkItemChangeCustomerDialogProps> = ({
  open,
  workItem,
  onClose,
  onChanged,
}) => {
  const [selected, setSelected] = useState<CustomerIndexItemDto | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelected(null);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const invoiceCount = workItem.invoices?.length ?? 0;
  const rsCustomerId = selected ? Number(selected.repairShoprCustomerId) : NaN;
  const canSubmit =
    !submitting &&
    selected != null &&
    Number.isFinite(rsCustomerId) &&
    rsCustomerId > 0 &&
    !!selected.dataverseCustomerId;

  const handleSubmit = async () => {
    if (!selected) return;
    if (!Number.isFinite(rsCustomerId) || rsCustomerId <= 0) {
      setError('The selected customer has no RepairShopr link and cannot be used.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const updated = await changeWorkItemCustomer(workItem.guid, {
        rsCustomerId,
        dataverseCustomerId: selected.dataverseCustomerId,
      });
      onChanged(updated);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change customer');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Change customer</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Reassign ticket <strong>#{workItem.id}</strong> from{' '}
          <strong>{workItem.customerName || 'the current customer'}</strong> to a
          different customer. This updates RepairShopr, then SM4RT.
        </DialogContentText>

        <Box sx={{ mt: 1 }}>
          <CustomerLookupControl
            value={selected}
            onChange={setSelected}
            autoFocus
            disabled={submitting}
          />
        </Box>

        {invoiceCount > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {invoiceCount} invoice{invoiceCount === 1 ? '' : 's'} already raised from
            this ticket will keep their current customer.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} sx={{ color: '#888' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={PRIMARY_BTN_SX}
        >
          {submitting ? 'Changing…' : 'Change customer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
